import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Ban, RotateCcw, Search } from "lucide-react";
import { NoAccess } from "../equipment/shared/NoAccess";

const MANAGE_ROLES = ["Director", "Manager", "Back Office"];
const VIEW_ROLES = [...MANAGE_ROLES, "Accounts"];

// Configurations > Bank Master. Director/Manager/Back Office manage;
// Accounts views read-only. Deactivate only — banks are never deleted.
export default function BankMasterPage() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [search, setSearch] = useState("");
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    client
      .get("/configurations/banks", { params: { include_inactive: includeInactive } })
      .then((res) => setItems(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (VIEW_ROLES.includes(user?.role)) load(); }, [includeInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? items.filter((b) => b.name.toLowerCase().includes(q)) : items;
  }, [items, search]);

  if (!VIEW_ROLES.includes(user?.role)) return <NoAccess />;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      toast.success(typeof okMsg === "function" ? okMsg(res) : okMsg);
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return toast.error("Bank Name is required");
    if (await run(() => client.post("/configurations/banks", { name: newName.trim() }), "Bank added")) {
      setNewName("");
      load();
    }
  };

  const rename = async (id) => {
    if (!editName.trim()) return toast.error("Bank Name cannot be empty");
    const ok = await run(
      () => client.put(`/configurations/banks/${id}`, { name: editName.trim() }),
      (res) => (res.data.vendors_updated ? `Bank updated — ${res.data.vendors_updated} vendor(s) now use the new name` : "Bank updated")
    );
    if (ok) {
      setEditing(null);
      load();
    }
  };

  const toggleActive = async (b) => {
    if (await run(() => client.post(`/configurations/banks/${b.id}/${b.active ? "deactivate" : "reactivate"}`), b.active ? "Bank deactivated" : "Bank reactivated")) load();
  };

  return (
    <div className="space-y-6" data-testid="bank-master-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          Bank Master
        </h1>
        <p className="text-sm text-slate-500">Banks offered in the Vendor form. Deactivated banks stay on existing vendors but are no longer offered.</p>
      </div>

      {canManage && (
        <form onSubmit={addItem} className="flex gap-2 max-w-lg">
          <Input data-testid="bank-master-new-name-input" placeholder="New Bank Name" value={newName} onChange={(e) => setNewName(e.target.value)} />
          <Button type="submit" disabled={busy} data-testid="bank-master-add-button" className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add
          </Button>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-8 h-9" placeholder="Search bank name" data-testid="bank-master-search-input" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button
          data-testid="bank-master-include-inactive-toggle"
          onClick={() => setIncludeInactive((v) => !v)}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
            includeInactive ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          {includeInactive ? "Showing All" : "Showing Active Only"}
        </button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="bank-master-table">
          <TableHeader>
            <TableRow>
              <TableHead>Bank Name</TableHead>
              <TableHead>Status</TableHead>
              {canManage && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
            ) : shown.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-400" data-testid="bank-master-empty">No banks found.</TableCell></TableRow>
            ) : (
              shown.map((b) => (
                <TableRow key={b.id} data-testid={`bank-master-row-${b.id}`}>
                  <TableCell>
                    {editing === b.id ? (
                      <Input className="h-8 max-w-sm" data-testid={`bank-master-edit-name-input-${b.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} />
                    ) : (
                      <span className="font-medium">{b.name}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={b.active ? "bg-emerald-600" : "bg-slate-400"}>{b.active ? "Active" : "Inactive"}</Badge>
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right space-x-2">
                      {editing === b.id ? (
                        <>
                          <Button size="sm" disabled={busy} data-testid={`bank-master-save-${b.id}`} onClick={() => rename(b.id)} className="bg-amber-600 hover:bg-amber-700">Save</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" data-testid={`bank-master-edit-${b.id}`} onClick={() => { setEditing(b.id); setEditName(b.name); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant={b.active ? "destructive" : "outline"} disabled={busy} data-testid={`bank-master-toggle-active-${b.id}`} onClick={() => toggleActive(b)}>
                            {b.active ? <Ban className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                          </Button>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
